import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// "템플릿에서 새 페이지" — 사이드바 진입점 (마이그레이션 0023, workspace.page_templates).
// 팝오버로 org 템플릿 목록을 보여주고, 선택 시 onSelect(template) 호출
// (실제 페이지 생성/본문 복사는 Sidebar 가 수행). 본인 생성 or 관리자는 삭제 가능.
// 테이블 미존재(0023 미적용) 시 안내 문구만 표시.
export default function TemplatePicker({ onSelect }) {
  const { user, membership } = useAuth();
  const orgId = membership?.org_id ?? null;
  const isAdmin = !!membership && (membership.role === 'owner' || membership.role === 'admin');

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(''); // '' | 'migration' | 'error'
  const [busy, setBusy] = useState(false);  // 템플릿에서 페이지 생성 중
  const menuRef = useRef(null);

  async function fetchTemplates() {
    if (!orgId) return;
    setLoading(true);
    setStatus('');
    try {
      const { data, error } = await supabase
        .schema('workspace')
        .from('page_templates')
        .select('id, name, icon, ystate_base64, created_by')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });
      if (error) {
        const missing = error.code === '42P01' || error.code === 'PGRST205'
          || /page_templates/i.test(error.message || '');
        setStatus(missing ? 'migration' : 'error');
        setTemplates([]);
      } else {
        setTemplates(data || []);
      }
    } catch (e) {
      setStatus('error');
      setTemplates([]);
    }
    setLoading(false);
  }

  function toggleOpen(e) {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: r.left + 8, y: r.bottom + 4 });
    setOpen(true);
    fetchTemplates();
  }

  // 바깥 클릭 / ESC 로 닫기 (사이드바 컨텍스트 메뉴와 동일 패턴 — 토글/메뉴 내부는 stopPropagation)
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function pick(tpl) {
    if (busy) return;
    setBusy(true);
    try {
      await onSelect(tpl);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  async function removeTemplate(tpl, e) {
    e.stopPropagation();
    const ok = window.confirm(`템플릿 "${tpl.name || '이름 없음'}"을(를) 삭제할까요?`);
    if (!ok) return;
    const { error } = await supabase
      .schema('workspace')
      .from('page_templates')
      .delete()
      .eq('id', tpl.id);
    if (error) {
      window.alert('템플릿을 삭제하지 못했습니다. 본인이 만든 템플릿 또는 관리자만 삭제할 수 있습니다.');
      return;
    }
    setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
  }

  return (
    <>
      <div className="nav-item nav-newpage" onClick={toggleOpen}>
        <span className="nav-tw spacer" />
        <span className="ic">📑</span>
        <span className="nav-label">템플릿에서 새 페이지</span>
      </div>

      {open && (
        <div
          className="nav-menu"
          ref={menuRef}
          style={{ left: pos.x, top: pos.y, minWidth: 220, maxHeight: 300, overflowY: 'auto' }}
          onClick={(e) => e.stopPropagation()}
        >
          {loading ? (
            <div className="nav-submenu-empty">불러오는 중…</div>
          ) : status === 'migration' ? (
            <div className="nav-submenu-empty">
              템플릿 기능을 사용하려면 마이그레이션 0023 적용이 필요합니다.
            </div>
          ) : status === 'error' ? (
            <div className="nav-submenu-empty">템플릿을 불러오지 못했습니다.</div>
          ) : templates.length === 0 ? (
            <div className="nav-submenu-empty">
              저장된 템플릿이 없습니다. 페이지 ⋯ 메뉴의 "템플릿으로 저장"으로 만들 수 있어요.
            </div>
          ) : (
            templates.map((t) => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  type="button"
                  className="mi"
                  style={{ flex: 1, minWidth: 0 }}
                  disabled={busy}
                  onClick={() => pick(t)}
                >
                  <span className="mi-ic">{t.icon || '📑'}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.name || '이름 없는 템플릿'}
                  </span>
                </button>
                {(isAdmin || t.created_by === user?.id) && (
                  <button
                    type="button"
                    className="mi danger"
                    style={{ width: 'auto', flexShrink: 0, padding: '6px 8px' }}
                    title="템플릿 삭제"
                    onClick={(e) => removeTemplate(t, e)}
                  >
                    🗑
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
}
